import React, { useState } from 'react';
import useSWR from 'swr';
import RootAdmin from '../../../components/layouts/RootAdmin';
import {
    Button,
    Card,
    CardBody,
    Chip,
    DatePicker,
    Dropdown,
    DropdownItem,
    DropdownMenu,
    DropdownTrigger,
    Spinner,
} from '@nextui-org/react';
import { BellAlertIcon, ClockIcon, FunnelIcon } from '@heroicons/react/24/outline';
import HeaderPage from '../../../components/elements/HeaderPage';
import { HIGHCHART_URL, getFetcher } from '../../../services/api';
import { BarChart, PieChart } from '@mui/x-charts';
import { getCountryName } from '../../../services/helpers';
import Highcharts from "highcharts";
import HighchartsReact from 'highcharts-react-official';
import highchartsExporting from 'highcharts/modules/exporting';

// Initialize Highcharts exporting modules
highchartsExporting(Highcharts);

export default function DashboardPage() {
    const [startDate, setStartDate] = useState(new Date());
    const [endDate, setEndDate] = useState(new Date());
    const handleStartDateChange = (date) => {
        setStartDate(new Date(date)); // Pastikan nilai menjadi objek Date
    };
    
    const handleEndDateChange = (date) => {
        setEndDate(new Date(date)); // Pastikan nilai menjadi objek Date
    };

    const { data, error, isLoading } = useSWR(
        `/api/dashboard-notifikasi?startDate=${startDate.toISOString()}&endDate=${endDate.toISOString()}`,
        getFetcher
    );

    const {
        totalNotifikasi,
        statusData,
        companyData,
        bahanB3Data,
        negaraData,
        averageProcessingDays,
    } = data || {
        totalNotifikasi: 0,
        statusData: [],
        companyData: [],
        bahanB3Data: [],
        negaraData: [],
        averageProcessingDays: 0,
    };

        // Highcharts configuration
        const getBarChartOptions = (title, categories, data) => ({
            chart: {
                type: 'column',
            },
            title: {
                text: title,
            },
            xAxis: {
                categories,
                title: {
                    text: null,
                },
            },
            yAxis: {
                min: 0,
                title: {
                    text: 'Jumlah',
                    align: 'high',
                },
                labels: {
                    overflow: 'justify',
                },
            },
            series: [
                {
                    name: 'Jumlah',
                    data,
                },
            ],
            exporting: {
                url: HIGHCHART_URL,
                enabled: true,
                showTable: true,
            },
        });
    
        const getPieChartOptions = (title, data) => ({
            chart: {
                type: 'pie',
            },
            title: {
                text: title,
            },
            series: [
                {
                    name: 'Jumlah',
                    colorByPoint: true,
                    data,
                },
            ],
            exporting: {
                url: HIGHCHART_URL,
                enabled: true,
                showTable: true,
            },
        });

    return (
        <RootAdmin>
            <HeaderPage
                title="Dashboard Notifikasi"
                subtitle="Gambaran Umum"
                action={
                    <FilterForm
                        startDate={startDate}
                        setStartDate={setStartDate}
                        endDate={endDate}
                        setEndDate={setEndDate}
                    />
                }
            />
            <div className="grid grid-cols-1 md:grid-cols-4 gap-3 pt-5">
                {/* Total Notifikasi */}
                <Card radius="sm" className="col-span-2">
                    <CardBody className="h-full flex flex-col items-center justify-center group">
                        {isLoading ? (
                            <Spinner size="lg" />
                        ) : (
                            <>
                                <BellAlertIcon className="size-20 stroke-ink-300 transform group-hover:rotate-12 ease-out duration-300" />
                                <h4 className="text-medium font-medium text-ink-400">Total Notifikasi</h4>
                                <h1 className="text-5xl font-semibold pt-4 pb-2">{totalNotifikasi}</h1>
                                <Chip
                                    size="sm"
                                    variant="flat"
                                    color="success"
                                    startContent={<ClockIcon className="size-4" />}
                                >
                                    dengan rata-rata proses {averageProcessingDays.toFixed(1)} hari
                                </Chip>
                            </>
                        )}
                    </CardBody>
                </Card>

                {/* Grafik Status Notifikasi */}
                <Card radius="sm" className="col-span-4">
                    <CardBody>
                        <h4 className="font-semibold text-lg mb-3">Grafik Status Notifikasi</h4>
                        {isLoading ? (
                            <Spinner size="lg" />
                        ) : (
                            <HighchartsReact
                                highcharts={Highcharts}
                                options={getBarChartOptions(
                                    'Distribusi Status Notifikasi',
                                    statusData?.map((item) => item.status),
                                    statusData?.map((item) => item._count.status)
                                )}
                            />
                        )}
                    </CardBody>
                </Card>

                {/* Grafik Perusahaan */}
                <Card radius="sm" className="col-span-4">
                    <CardBody>
                        <h4 className="font-semibold text-lg mb-3">Grafik Notifikasi Per Perusahaan</h4>
                        {isLoading ? (
                            <Spinner size="lg" />
                        ) : (
                            <HighchartsReact
                                highcharts={Highcharts}
                                options={getBarChartOptions(
                                    'Notifikasi Per Perusahaan',
                                    companyData?.map((item) => item.companyName),
                                    companyData?.map((item) => item._count.companyId)
                                )}
                            />
                        )}
                    </CardBody>
                </Card>

                {/* Grafik Jenis B3 */}
                <Card radius="sm" className="col-span-4">
                    <CardBody>
                        <h4 className="font-semibold text-lg mb-3">Jenis B3 Terbanyak</h4>
                        {isLoading ? (
                            <Spinner size="lg" />
                        ) : (
                            <HighchartsReact
                                highcharts={Highcharts}
                                options={getPieChartOptions(
                                    'Jenis B3 Terbanyak',
                                    bahanB3Data?.map((item) => ({
                                        name: `${item.casNumber}/${item.namaBahanKimia}`,
                                        y: item._count.dataBahanB3Id,
                                    }))
                                )}
                            />
                        )}
                    </CardBody>
                </Card>

                {/* Grafik Negara Asal */}
                <Card radius="sm" className="col-span-4">
                    <CardBody>
                        <h4 className="font-semibold text-lg mb-3">Grafik Negara Asal</h4>
                        {isLoading ? (
                            <Spinner size="lg" />
                        ) : (
                            <HighchartsReact
                                highcharts={Highcharts}
                                options={getBarChartOptions(
                                    'Distribusi Negara Asal',
                                    negaraData?.map((item) =>
                                        getCountryName(item.negaraAsal, 'en', { select: 'official' })
                                    ),
                                    negaraData?.map((item) => item._count.negaraAsal)
                                )}
                            />
                        )}
                    </CardBody>
                </Card>
            </div>
        </RootAdmin>
    );
}

function FilterForm({ startDate, setStartDate, endDate, setEndDate }) {
    const handleStartDateChange = (date) => {
        setStartDate(new Date(date));
    };

    const handleEndDateChange = (date) => {
        setEndDate(new Date(date));
    };

    return (
        <Dropdown closeOnSelect={false}>
            <DropdownTrigger>
                <Button radius="sm" variant="bordered" startContent={<FunnelIcon className="size-4" />}>
                    Saring
                </Button>
            </DropdownTrigger>
            <DropdownMenu aria-label="Filter">
                <DropdownItem isReadOnly>
                    <DatePicker
                        size="sm"
                        label="Tanggal Mulai"
                        labelPlacement="outside"
                        selected={startDate}
                        onChange={handleStartDateChange}
                    />
                </DropdownItem>
                <DropdownItem isReadOnly>
                    <DatePicker
                        size="sm"
                        label="Tanggal Akhir"
                        labelPlacement="outside"
                        selected={endDate}
                        onChange={handleEndDateChange}
                    />
                </DropdownItem>
                <DropdownItem isReadOnly>
                    <Button size="sm" color="primary" onClick={() => console.log('Filter applied')}>
                        Terapkan
                    </Button>
                </DropdownItem>
            </DropdownMenu>
        </Dropdown>
    );
}
